import type ExchangeTierInterface from 'app/interfaces/shop/ExchangeTierInterface';
import {clamp} from 'app/utils/clamp';

export const EXCHANGE_MIN_POINTS = 1000;
export const EXCHANGE_STEP = 500;
export const EXCHANGE_SCALE_MAX = 50000;
const PERCENT_BASE = 100;
const MONEY_DECIMALS = 2;
const THOUSANDS_GROUP_SIZE = 3;

export const EXCHANGE_TIERS: ExchangeTierInterface[] = [
    {bonusPercent: 0, points: EXCHANGE_MIN_POINTS},
    {bonusPercent: 3, points: 5000},
    {bonusPercent: 5, points: 10000},
    {bonusPercent: 7, points: 20000},
    {bonusPercent: 10, points: EXCHANGE_SCALE_MAX}
];

export const EXCHANGE_SLIDER_TICKS: ExchangeTierInterface[] = EXCHANGE_TIERS.filter(
    (tier: ExchangeTierInterface) => tier.bonusPercent > 0
);

export const bonusPercentFor = (points: number): number => [...EXCHANGE_TIERS]
    .reverse()
    .find((tier: ExchangeTierInterface) => points >= tier.points)?.bonusPercent ?? 0;

export const activeTickPointsFor = (points: number): number | null => [...EXCHANGE_SLIDER_TICKS]
    .reverse()
    .find((tier: ExchangeTierInterface) => points >= tier.points)?.points ?? null;

export const percentOfScale = (points: number): number => {
    const percent = (points - EXCHANGE_MIN_POINTS) / (EXCHANGE_SCALE_MAX - EXCHANGE_MIN_POINTS) * PERCENT_BASE;

    return clamp(percent, 0, PERCENT_BASE);
};

export const moneyForPoints = (points: number): number => {
    const bonus = bonusPercentFor(points);

    return Number((points / EXCHANGE_MIN_POINTS * (1 + bonus / PERCENT_BASE)).toFixed(MONEY_DECIMALS));
};

export const canExchange = (points: number | string): boolean => Number(points) >= EXCHANGE_MIN_POINTS;

const maxExchangeablePoints = (balance: number): number => {
    if (!canExchange(balance)) {
        return 0;
    }

    return Math.floor(balance / EXCHANGE_STEP) * EXCHANGE_STEP;
};

export const nextTier = (points: number): ExchangeTierInterface | null => EXCHANGE_SLIDER_TICKS.find(
    (tier: ExchangeTierInterface) => tier.points > points
) ?? null;

export const defaultSelectedPoints = (balance: number): number => {
    const maxPoints = maxExchangeablePoints(balance);

    if (maxPoints === 0) {
        return EXCHANGE_MIN_POINTS;
    }

    return Math.min(maxPoints, EXCHANGE_SLIDER_TICKS[0].points);
};

export const formatPointsAmount = (value: number): string => {
    const digits = Math.trunc(value).toString();
    const headLength = digits.length % THOUSANDS_GROUP_SIZE;
    const groups = headLength > 0
        ? [digits.slice(0, headLength)]
        : [];

    for (let index = headLength; index < digits.length; index += THOUSANDS_GROUP_SIZE) {
        groups.push(digits.slice(index, index + THOUSANDS_GROUP_SIZE));
    }

    return groups.join(' ');
};
